import refreshIcon from '@raison/static/icons/refresh-line.svg';
import {InlineSvg, RootLayout, RsnButton, RsnDialog, useBoolean} from '@raison/ui-kit';
import React, {Fragment, useMemo} from 'react';
import {ShellVersion} from '~/entities/versions';
import {ShellEventBus} from '~/shared/shell-bridge/ShellEventBus';
import {isIOS} from '~/shared/utils/mobileDeviceDetect';

const text = {
    TitleCrucial: 'Update required',
    TitleOptimal: 'Update recommended',
    Action: 'Update now',
    Prompt: 'Update app',
    Dismis: 'Later',
    canUpdate: {
        true: {
            MessageCrucial: 'This version of the app is no longer supported. Please update the app to continue.',
            MessageOptimal:
                'This version of the app will soon stop being supported. Please update the app to avoid interruptions.',
        },
        false: {
            MessageCrucial:
                'This version of the app is no longer supported. Please update the app in the App Store or Google Play to continue.',
            MessageOptimal:
                'This version of the app will soon stop being supported. Please update the app in the App Store or Google Play to avoid interruptions.',
        },
    },
};

function getUpdateHandler() {
    const current = ShellVersion.getFromWindow();
    let supportingVersion = '2026.0';
    if (isIOS()) {
        supportingVersion = '2026.2';
    }
    if (!current || ShellVersion.compare(current, '<', supportingVersion)) {
        return null;
    }
    if (ShellEventBus.supports('ShellAppUpdateIntent')) {
        return () => {
            ShellEventBus.send('ShellAppUpdateIntent');
        };
    }
    if (isIOS()) {
        return () => {
            open('https://apps.apple.com/app/1444995068');
        };
    } else {
        return () => {
            open('https://play.google.com/store/apps/details?id=ai.raison.app');
        };
    }
}

export function ShellVersionFallback() {
    const handleUpdate = useMemo(getUpdateHandler, []);
    const t = text.canUpdate[`${!!handleUpdate}`];

    return (
        <main className='flex h-full flex-col'>
            <header className='grid h-16 shrink-0 place-items-center'>
                <RootLayout.CompanyLogoImg width={62} height={64} />
            </header>
            <div className='flex flex-1 flex-col gap-6 p-4 pb-8'>
                <div className='flex flex-1 flex-col items-center justify-center gap-6 text-center'>
                    <figure className='flex size-24 items-center justify-center rounded-full bg-neutral-20'>
                        <InlineSvg src={refreshIcon} className='size-[60px]' />
                    </figure>
                    <hgroup className='grid gap-2'>
                        <h2 className='fv-heading-l text-neutral-100'>{text.TitleCrucial}</h2>
                        <p className='fv-text-l-regular text-neutral-90'>{t.MessageCrucial}</p>
                    </hgroup>
                </div>
                {handleUpdate && (
                    <footer>
                        <RsnButton.Primary onClick={handleUpdate}>{text.Action}</RsnButton.Primary>
                    </footer>
                )}
            </div>
        </main>
    );
}

export function ShellVersionFallbackDialog() {
    const handleUpdate = useMemo(getUpdateHandler, []);
    const t = text.canUpdate[`${!!handleUpdate}`];

    // TODO: RAISON-7015 align with error dialogs design
    return (
        <RsnDialog.Window>
            <RsnDialog.CenteredHeader icon={RsnDialog.framedIcon(refreshIcon)}>
                {text.TitleOptimal}
            </RsnDialog.CenteredHeader>
            <RsnDialog.Body>{t.MessageOptimal}</RsnDialog.Body>
            <RsnDialog.Footer column>
                {handleUpdate && <RsnButton.Primary onClick={handleUpdate}>{text.Action}</RsnButton.Primary>}
                <RsnButton.Alike intent='secondary' as={RsnDialog.CloseTrigger}>
                    {text.Dismis}
                </RsnButton.Alike>
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );
}

export function VersionUpdateFloatingPane() {
    ShellVersion.useSubscription();

    const [isOpen, setOpen] = useBoolean();
    if (!ShellVersion.isOptimalOutdated()) {
        return null;
    }
    return (
        <Fragment>
            <div className='pointer-events-none fixed bottom-8 left-0 right-0 flex justify-center'>
                <button
                    onClick={setOpen.on}
                    className='fv-text-l-bold pointer-events-auto h-11 rounded-lg bg-neutral-100 px-4 text-neutral-10'
                >
                    {text.Prompt}
                </button>
            </div>
            <RsnDialog.Controller isOpen={isOpen} onClose={setOpen.off}>
                <ShellVersionFallbackDialog />
            </RsnDialog.Controller>
        </Fragment>
    );
}
